import { axiosInstanceWebsite, axiosInstanceWhatsAppConfirmation } from "../instance";

// ---- Types ----

export interface WhatsAppAccount {
  id: number;
  owner_id: string;
  phone_number_id: string;
  waba_id: string;
  display_name: string | null;
  is_active: boolean;
  confirmation_template_name?: string | null;
  reminder_template_name?: string | null;
  fallback_template_name?: string | null;
  cart_recovery_template_name?: string | null;
  order_confirmed_template_name?: string | null;
}

export interface CreateWhatsAppAccountRequest {
  owner_id: string;
  phone_number_id: string;
  waba_id: string;
  access_token: string;
  app_secret?: string;
  verify_token: string;
  display_name?: string;
}

export interface Confirmation {
  id: number;
  order_id: number;
  order_hash_id: string;
  customer_name: string | null;
  customer_phone: string;
  status: ConfirmationStatus;
  retry_count: number;
  created_at: string | null;
  confirmed_at: string | null;
}

export type ConfirmationStatus =
  | "pending"
  | "awaiting_response"
  | "confirmed"
  | "rejected"
  | "expired"
  | "canceled"
  | "send_failed";

export interface ConversationMessage {
  role: "assistant" | "user";
  content: string;
  timestamp: string;
  action?: string | null;
  message_type?: "template" | "text";
  status?: "sending" | "sent" | "delivered" | "read" | "failed";
  media_url?: string | null;
}

export interface Conversation {
  id: number;
  customer_phone: string;
  messages: ConversationMessage[];
  is_active: boolean;
  ai_enabled: boolean;
  started_at: string | null;
  ended_at: string | null;
}

export interface ReportsSummary {
  owner_id: string;
  total: number;
  by_status: Record<string, number>;
  confirmation_rate: number;
}

// ---- API functions ----

export const getWhatsAppAccount = async (
  ownerId: string
): Promise<WhatsAppAccount> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    `/admin/whatsapp-accounts/${ownerId}`
  );
  return response.data;
};

export const createWhatsAppAccount = async (
  data: CreateWhatsAppAccountRequest
) => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    "/admin/whatsapp-accounts",
    data
  );
  return response.data;
};

export const getConfirmations = async (
  ownerId: string,
  status?: ConfirmationStatus,
  limit: number = 50,
  offset: number = 0
): Promise<Confirmation[]> => {
  const params: Record<string, string | number> = {
    owner_id: ownerId,
    limit,
    offset,
  };
  if (status) params.status = status;
  const response = await axiosInstanceWhatsAppConfirmation.get(
    "/admin/confirmations",
    { params }
  );
  return response.data;
};

export const getConversation = async (
  confirmationId: number
): Promise<Conversation> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    `/admin/confirmations/${confirmationId}/conversation`
  );
  return response.data;
};

export const getReportsSummary = async (
  ownerId: string
): Promise<ReportsSummary> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    "/admin/reports/summary",
    { params: { owner_id: ownerId } }
  );
  return response.data;
};

export const resendConfirmation = async (
  confirmationId: number
): Promise<{ status: string; order_id: number }> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    `/admin/confirmations/${confirmationId}/resend`
  );
  return response.data;
};

// ---- AI Toggle & Manual Messaging ----

export const toggleAIMode = async (
  confirmationId: number,
  aiEnabled: boolean
): Promise<{ confirmation_id: number; ai_enabled: boolean }> => {
  const response = await axiosInstanceWhatsAppConfirmation.patch(
    `/admin/conversations/${confirmationId}/ai-toggle`,
    { ai_enabled: aiEnabled }
  );
  return response.data;
};

export const sendManualMessage = async (
  confirmationId: number,
  message: string
): Promise<{ status: string; messages: ConversationMessage[] }> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    `/admin/conversations/${confirmationId}/send-message`,
    { message }
  );
  return response.data;
};

// ---- Batch Status (for orders table & detail) ----

export interface ConfirmationInfo {
  status: ConfirmationStatus;
  confirmation_id: number;
  ai_enabled: boolean;
}

export const getConfirmationStatuses = async (
  ownerId: string,
  orderIds: number[]
): Promise<Record<string, ConfirmationInfo>> => {
  if (orderIds.length === 0) return {};
  const response = await axiosInstanceWhatsAppConfirmation.post(
    "/admin/confirmations/batch-status",
    { owner_id: ownerId, order_ids: orderIds }
  );
  return response.data.statuses;
};

// ---- Update Address (manual from order detail) ----

export const updateShippingAddress = async (
  confirmationId: number,
  addressData: { address?: string; addressDetail?: string; city?: string; department?: string }
): Promise<{ status: string; fields: string[] }> => {
  const response = await axiosInstanceWhatsAppConfirmation.put(
    `/admin/confirmations/${confirmationId}/update-address`,
    addressData
  );
  return response.data;
};

// ---- Manual Order Actions ----

export const confirmOrderManual = async (
  confirmationId: number
): Promise<{ status: string; order_id: number }> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    `/admin/confirmations/${confirmationId}/confirm`
  );
  return response.data;
};

export const cancelOrderManual = async (
  confirmationId: number
): Promise<{ status: string; order_id: number }> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    `/admin/confirmations/${confirmationId}/cancel`
  );
  return response.data;
};

// ---- Delete Conversation (Soft Delete) ----

export const deleteConversation = async (
  confirmationId: number
): Promise<{ status: string; confirmation_id: number }> => {
  const response = await axiosInstanceWhatsAppConfirmation.delete(
    `/admin/confirmations/${confirmationId}/conversation`
  );
  return response.data;
};

// ---- Customer Aggregation (unified chat) ----

export interface CustomerSummary {
  customer_phone: string;
  customer_name: string | null;
  alias: string | null;
  display_name: string;
  tags: string[];
  order_count: number;
  latest_status: ConfirmationStatus;
  latest_created_at: string | null;
  latest_confirmation_id: number;
  latest_order_id: number;
  ai_enabled: boolean;
}

export interface UnifiedMessage extends ConversationMessage {
  order_id: number;
  confirmation_id: number;
}

export interface CustomerOrder {
  confirmation_id: number;
  order_id: number;
  order_hash_id: string;
  status: ConfirmationStatus;
  ai_enabled: boolean;
  is_active: boolean;
  created_at: string | null;
}

export interface CustomerConversation {
  customer_phone: string;
  customer_name: string | null;
  orders: CustomerOrder[];
  messages: UnifiedMessage[];
  active_confirmation_id: number | null;
}

export interface CustomerConfirmation extends Confirmation {
  order_data: Record<string, any>;
  ai_enabled: boolean;
}

export interface KanbanData {
  columns: Record<string, KanbanCard[]>;
  totals: Record<string, number>;
}

export interface KanbanCard {
  id: number;
  order_id: number;
  order_hash_id: string;
  status: ConfirmationStatus;
  customer_name: string | null;
  customer_phone: string;
  ai_enabled: boolean;
  created_at: string | null;
  total: number;
  product_name: string;
}

export const getCustomers = async (
  ownerId: string,
  search?: string,
  limit: number = 50,
  offset: number = 0
): Promise<CustomerSummary[]> => {
  const params: Record<string, string | number> = { owner_id: ownerId, limit, offset };
  if (search) params.search = search;
  const response = await axiosInstanceWhatsAppConfirmation.get("/admin/customers", { params });
  return response.data;
};

export const getCustomerMessages = async (
  phone: string,
  ownerId: string
): Promise<CustomerConversation> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    `/admin/customers/${phone}/messages`,
    { params: { owner_id: ownerId } }
  );
  return response.data;
};

export const getCustomerConfirmations = async (
  phone: string,
  ownerId: string
): Promise<CustomerConfirmation[]> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    `/admin/customers/${phone}/confirmations`,
    { params: { owner_id: ownerId } }
  );
  return response.data;
};

export const updateConfirmationStatus = async (
  confirmationId: number,
  status: string
): Promise<{ status: string; order_id: number }> => {
  const response = await axiosInstanceWhatsAppConfirmation.patch(
    `/admin/confirmations/${confirmationId}/status`,
    { status }
  );
  return response.data;
};

export const getKanban = async (
  ownerId: string,
  limit: number = 200
): Promise<KanbanData> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    "/admin/kanban",
    { params: { owner_id: ownerId, limit } }
  );
  return response.data;
};

// ---- OAuth Embedded Signup ----

export interface WhatsAppOAuthCallbackRequest {
  code: string;
  phone_number_id: string;
  waba_id: string;
  owner_id: string;
}

export interface WhatsAppOAuthCallbackResponse {
  status: string;
  owner_id: string;
  phone_number_id: string;
  waba_id: string;
  verify_token: string;
}

export const whatsappOAuthCallback = async (
  data: WhatsAppOAuthCallbackRequest
): Promise<WhatsAppOAuthCallbackResponse> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    "/admin/whatsapp-accounts/oauth/callback",
    data
  );
  return response.data;
};

// ---- AI Configuration ----

export type TonePreset = "professional" | "friendly" | "casual" | "custom";
export type ResponseLength = "short" | "medium" | "long";

export interface WSAIConfig {
  tone_preset: TonePreset;
  custom_instructions: string;
  temperature: number;
  max_response_length: ResponseLength;
  ai_enabled_default: boolean;
  model_provider: string;
  model_name: string;
  prompt_preview?: string;
  billing_status?: {
    pending_credits: number;
    delinquency_limit: number;
    is_delinquent: boolean;
  };
}

export interface WSAIBillingSettleResponse {
  billing_status: NonNullable<WSAIConfig["billing_status"]>;
}

export interface AIModel {
  provider: string;
  model: string;
  display_name: string;
  input_cost_usd_per_1k: number;
  output_cost_usd_per_1k: number;
  credit_multiplier: number;
  estimated_credits_per_message: number;
}

export const listAIModels = async (
  responseLength: ResponseLength = "medium"
): Promise<AIModel[]> => {
  const response = await axiosInstanceWhatsAppConfirmation.get("/admin/ai-models", {
    params: { response_length: responseLength },
  });
  return response.data;
};

// ---- Confirmation per-funnel (ecommerce-service) ----

export interface FunnelProduct {
  product_id: string;
  type?: string;
}

export type WhatsAppConfirmationImageMode = "product" | "custom" | "none";

export interface FunnelWithWAFlag {
  id: number;
  name: string;
  brand_name: string;
  url?: string;
  idSequence?: number;
  description?: string;
  products?: FunnelProduct[];
  configurations?: { name_funnel?: string | null; [key: string]: unknown };
  whatsapp_confirmation_enabled: boolean;
  whatsapp_confirmation_image_mode: WhatsAppConfirmationImageMode;
  whatsapp_confirmation_image_url: string | null;
}

export const listFunnelsForWAConfirmation = async (): Promise<FunnelWithWAFlag[]> => {
  const response = await axiosInstanceWebsite.get("/websites");
  const websites: any[] = response.data || [];
  return websites.map((w) => ({
    id: w.id,
    name: w.name || w.brand_name || `Funnel ${w.id}`,
    brand_name: w.brand_name || "",
    url: w.url || undefined,
    idSequence: w.id_sequence_owner_id,
    description: w.sale_angle?.description || w.sales_angle_description || "",
    products: w.products || [],
    configurations: w.configurations || {},
    whatsapp_confirmation_enabled:
      w.whatsapp_confirmation_enabled ??
      w.configurations?.whatsapp_confirmation_enabled ??
      false,
    whatsapp_confirmation_image_mode:
      (w.configurations?.whatsapp_confirmation_image_mode as WhatsAppConfirmationImageMode | undefined) ??
      "product",
    whatsapp_confirmation_image_url:
      (w.configurations?.whatsapp_confirmation_image_url as string | undefined) ??
      null,
  }));
};

export const setFunnelWAConfirmation = async (
  websiteId: number,
  enabled: boolean,
  existingConfigurations: Record<string, unknown> = {},
  media?: {
    imageMode?: WhatsAppConfirmationImageMode;
    imageUrl?: string | null;
  }
): Promise<void> => {
  // ecommerce-service PUT /websites/{id} replaces the whole `configurations`
  // map, so we must merge the new flag on top of the existing map to avoid
  // wiping other keys (order_ip_restriction, dropi integration, etc).
  const mergedConfigurations = {
    ...existingConfigurations,
    whatsapp_confirmation_enabled: enabled,
    ...(media?.imageMode ? { whatsapp_confirmation_image_mode: media.imageMode } : {}),
    ...(media && "imageUrl" in media ? { whatsapp_confirmation_image_url: media.imageUrl ?? null } : {}),
  };
  await axiosInstanceWebsite.put(`/websites/${websiteId}`, {
    configurations: mergedConfigurations,
  });
};

// ---- WhatsApp account health ----

export interface WAAccountHealth {
  quality_rating: "GREEN" | "YELLOW" | "RED" | "UNKNOWN" | null;
  messaging_limit_tier: string | null;
  token_status: "valid" | "expired" | "unknown";
  token_expires_at: string | null;
  token_seconds_remaining: number | null;
  token_days_remaining: number | null;
  token_hours_remaining: number | null;
  token_minutes_remaining: number | null;
  token_expiring_soon: boolean;
  last_health_check_at: string | null;
}

export const getWAAccountHealth = async (
  ownerId: string
): Promise<WAAccountHealth> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    `/admin/whatsapp-accounts/${ownerId}/health`
  );
  return response.data;
};

export const refreshWAAccountHealth = async (
  ownerId: string
): Promise<WAAccountHealth> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    `/admin/whatsapp-accounts/${ownerId}/health/refresh`
  );
  return response.data;
};

// ---- Contacts ----

export interface ContactNote {
  author: "human" | "ai";
  content: string;
  created_at: string;
}

export interface ContactOrderHistoryItem {
  order_id: number;
  order_hash_id: string;
  status: string;
  total: number;
  product_name: string;
  created_at: string | null;
  confirmed_at: string | null;
}

export interface Contact {
  id: number;
  owner_id: string;
  phone: string;
  name: string | null;
  email: string | null;
  alias: string | null;
  notes: ContactNote[];
  tags: string[];
  created_at: string | null;
  updated_at: string | null;
}

export interface ContactDetail extends Contact {
  order_history: ContactOrderHistoryItem[];
  display_name: string;
}

export interface ContactCount {
  used: number;
  limit: number;
}

// All /admin/contacts* endpoints derive owner_id from the JWT (Bearer in
// the axios interceptor), so we no longer pass it as a query/body param.

export const getContactCount = async (): Promise<ContactCount> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    "/admin/contacts/count"
  );
  return response.data;
};

export const listContactTags = async (): Promise<string[]> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    "/admin/contacts/tags"
  );
  return response.data;
};

export const listContacts = async (
  search?: string,
  limit: number = 200,
  offset: number = 0,
  tag?: string
): Promise<Contact[]> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    "/admin/contacts",
    {
      params: {
        search: search || undefined,
        tag: tag || undefined,
        limit,
        offset,
      },
    }
  );
  return response.data;
};

export const getContactById = async (
  contactId: number
): Promise<ContactDetail> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    `/admin/contacts/${contactId}`
  );
  return response.data;
};

export const getContactByPhone = async (
  phone: string
): Promise<ContactDetail | null> => {
  const listRes = await axiosInstanceWhatsAppConfirmation.get("/admin/contacts", {
    params: { search: phone, limit: 5 },
  });
  const match = (listRes.data as Contact[]).find(
    (c) => c.phone === phone.replace(/^\+/, "")
  );
  if (!match) return null;
  const detailRes = await axiosInstanceWhatsAppConfirmation.get(
    `/admin/contacts/${match.id}`
  );
  return detailRes.data;
};

export const upsertContact = async (
  phone: string,
  name?: string | null,
  email?: string | null
): Promise<Contact> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    "/admin/contacts",
    {
      phone,
      name: name ?? undefined,
      email: email ?? undefined,
    }
  );
  return response.data;
};

export const updateContactAlias = async (
  contactId: number,
  alias: string | null
): Promise<Contact> => {
  const response = await axiosInstanceWhatsAppConfirmation.patch(
    `/admin/contacts/${contactId}`,
    { alias }
  );
  return response.data;
};

export const deleteContact = async (contactId: number): Promise<void> => {
  await axiosInstanceWhatsAppConfirmation.delete(
    `/admin/contacts/${contactId}`
  );
};

export const addContactNote = async (
  contactId: number,
  content: string,
  author: "human" | "ai" = "human"
): Promise<Contact> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    `/admin/contacts/${contactId}/notes`,
    { content, author }
  );
  return response.data;
};

export const setContactTags = async (
  contactId: number,
  tags: string[]
): Promise<Contact> => {
  const response = await axiosInstanceWhatsAppConfirmation.put(
    `/admin/contacts/${contactId}/tags`,
    { tags }
  );
  return response.data;
};

export const getWSAIConfig = async (
  ownerId: string
): Promise<WSAIConfig> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    `/admin/ai-config/${ownerId}`
  );
  return response.data;
};

export const updateWSAIConfig = async (
  ownerId: string,
  config: Partial<WSAIConfig>
): Promise<WSAIConfig> => {
  const response = await axiosInstanceWhatsAppConfirmation.put(
    `/admin/ai-config/${ownerId}`,
    config
  );
  return response.data;
};

export const settleWSAIBilling = async (
  ownerId: string
): Promise<WSAIBillingSettleResponse> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    `/admin/ai-config/${ownerId}/billing/settle`
  );
  return response.data;
};

// ---- WhatsApp Templates ----

export type TemplateStatus =
  | "APPROVED"
  | "PENDING"
  | "REJECTED"
  | "PAUSED"
  | "DISABLED"
  | "IN_APPEAL";

export type TemplateCategory = "UTILITY" | "MARKETING" | "AUTHENTICATION";

export interface WhatsAppTemplate {
  id: number;
  name: string;
  language: string;
  status: TemplateStatus;
  category: TemplateCategory;
  components: any[] | null;
  body_text: string | null;
  variable_count: number;
  rejection_reason: string | null;
  meta_template_id: string | null;
  last_synced_at: string | null;
  created_at: string | null;
}

export interface CreateTemplateRequest {
  owner_id: string;
  name: string;
  language: string;
  category: TemplateCategory;
  components: any[];
}

export interface UpdateTemplateRequest {
  owner_id: string;
  category: TemplateCategory;
  components: any[];
}

export const listTemplates = async (
  ownerId: string,
  status?: TemplateStatus
): Promise<WhatsAppTemplate[]> => {
  const params: Record<string, string> = { owner_id: ownerId };
  if (status) params.status = status;
  const response = await axiosInstanceWhatsAppConfirmation.get(
    "/admin/templates",
    { params }
  );
  return response.data;
};

export const syncTemplates = async (
  ownerId: string
): Promise<{ owner_id: string; synced: number; removed: number }> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    "/admin/templates/sync",
    { owner_id: ownerId }
  );
  return response.data;
};

export const createTemplate = async (
  request: CreateTemplateRequest
): Promise<WhatsAppTemplate> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    "/admin/templates",
    request
  );
  return response.data;
};

export const updateTemplate = async (
  templateId: number,
  request: UpdateTemplateRequest
): Promise<WhatsAppTemplate> => {
  const response = await axiosInstanceWhatsAppConfirmation.put(
    `/admin/templates/${templateId}`,
    request
  );
  return response.data;
};

export const deleteTemplate = async (
  ownerId: string,
  name: string
): Promise<{ status: string; name: string }> => {
  const response = await axiosInstanceWhatsAppConfirmation.delete(
    "/admin/templates",
    { params: { owner_id: ownerId, name } }
  );
  return response.data;
};

export interface DefaultTemplatesPayload {
  confirmation_template_name?: string | null;
  reminder_template_name?: string | null;
  fallback_template_name?: string | null;
  cart_recovery_template_name?: string | null;
  order_confirmed_template_name?: string | null;
}

export const setDefaultTemplates = async (
  ownerId: string,
  payload: DefaultTemplatesPayload
): Promise<{
  owner_id: string;
  confirmation_template_name: string | null;
  reminder_template_name: string | null;
  fallback_template_name: string | null;
  cart_recovery_template_name: string | null;
  order_confirmed_template_name: string | null;
}> => {
  const response = await axiosInstanceWhatsAppConfirmation.put(
    `/admin/whatsapp-accounts/${ownerId}/default-templates`,
    payload
  );
  return response.data;
};

// ---- Realtime config (so frontend uses the backend's exact ENV prefix) ----

export interface RealtimeConfig {
  env: string;
  firebase_prefix: string;
}

export const getRealtimeConfig = async (): Promise<RealtimeConfig> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    "/admin/realtime-config"
  );
  return response.data;
};

// ---- Conversation 24h Window + Template fallback ----

export interface ConversationWindowState {
  last_inbound_at: string | null;
  within_24h_window: boolean;
  seconds_remaining: number | null;
  customer_responded: boolean;
}

export const getConversationWindow = async (
  confirmationId: number
): Promise<ConversationWindowState> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    `/admin/conversations/${confirmationId}/window`
  );
  return response.data;
};

export interface SendConversationTemplateRequest {
  template_name: string;
  language?: string;
  variables: string[];
}

export const sendConversationTemplate = async (
  confirmationId: number,
  payload: SendConversationTemplateRequest
): Promise<{
  status: string;
  template_name: string;
  wa_message_id: string;
  messages: ConversationMessage[];
}> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    `/admin/conversations/${confirmationId}/send-template`,
    payload
  );
  return response.data;
};

// ---- Abandoned Cart Recovery ----

export interface RecoverAbandonedCartProduct {
  name?: string | null;
  units?: number | null;
  total_value_with_discount?: number | null;
}

export interface RecoverAbandonedCartRequest {
  owner_id: string;
  phone: string;
  customer_name?: string | null;
  funnel_name?: string | null;
  products: RecoverAbandonedCartProduct[];
}

export interface RecoverAbandonedCartResponse {
  status: string;
  phone: string;
  template_name: string;
  wa_message_id: string;
}

export const recoverAbandonedCart = async (
  payload: RecoverAbandonedCartRequest
): Promise<RecoverAbandonedCartResponse> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    "/admin/abandoned-carts/recover",
    payload
  );
  return response.data;
};
